import React, { useRef } from "react";
import Cancel from "../../assest/svg/cancel.svg";
import Clip from "../../assest/svg/clip.svg";
import ClipEngage from "../../assest/svg/clip_engage.svg";
import FileIcon from "../../assest/svg/file.svg";

type InputProps = {
  onSubmit: (message: string) => void;
  loading: boolean;
  disabled?: boolean;
  setInput: (input: string) => void;
  setImage: (image: File[]) => void;
  input: string;
  image: File[];
  cancelApi: () => void;
  fetching: boolean;
};

export const Input: React.FC<InputProps> = ({
  loading,
  onSubmit,
  setInput,
  setImage,
  input,
  image,
  cancelApi,
  fetching,
}) => {
  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const handleSend = () => {
    if (!input.trim() && (!image || image.length === 0)) return;
    onSubmit(input);
    setInput("");
    setImage([]);
  };

  const handleAddImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setImage([...(image ?? []), ...Array.from(event.target.files)]);
    }
  };

  const handleRemoveImage = (index: number) => {
    setImage(image.filter((_, i) => i !== index));
  };

  return (
    <div className="w-full flex flex-col items-center">
      {/* Streaming Floating Status Pill when fetching */}
      {fetching && (
        <div className="mb-2 inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-[#131622]/90 dark:bg-[#131622]/90 border border-purple-500/30 text-xs shadow-lg animate-fadeIn text-slate-200">
          <span className="w-2 h-2 rounded-full bg-purple-500 animate-ping" />
          <span>Génération en cours... Vous pouvez interrompre l'exécution à tout moment</span>
          <button
            type="button"
            onClick={cancelApi}
            className="ml-1 px-2 py-0.5 rounded-md bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 font-semibold text-[11px] transition-colors"
          >
            Arrêter
          </button>
        </div>
      )}

      {/* Main Input Capsule */}
      <div className="w-full bg-white dark:bg-[#131622] backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-2xl shadow-xl p-2.5 focus-within:border-purple-500/50 focus-within:ring-1 focus-within:ring-purple-500/20 transition-all duration-200">
        {/* Attached Files Tray */}
        {image && image.length > 0 && (
          <div className="flex flex-wrap gap-2 pb-2.5 mb-2 border-b border-slate-100 dark:border-white/5">
            {Array.from(image).map((item, index) => {
              const fileType = item.type.split("/")[1] ?? "";
              const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(fileType);

              return (
                <div
                  key={index}
                  className="relative group flex items-center gap-2 p-1.5 pr-6 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-xs text-slate-700 dark:text-slate-300"
                >
                  {isImage ? (
                    <img
                      className="w-8 h-8 rounded-lg object-cover"
                      src={URL.createObjectURL(item)}
                      alt="Aperçu"
                    />
                  ) : (
                    <img
                      src={FileIcon}
                      className="w-5 h-5 dark:invert opacity-70"
                      alt="Fichier"
                    />
                  )}
                  <span className="max-w-[120px] truncate">{item.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-slate-300 dark:hover:bg-white/20 transition-colors"
                    title="Supprimer"
                  >
                    <img src={Cancel} alt="x" className="w-3.5 h-3.5 opacity-60 hover:opacity-100 dark:invert" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Input Bar Row */}
        <div className="flex items-center gap-2">
          {/* File Attachment Button */}
          <input
            type="file"
            className="hidden"
            disabled={loading}
            onChange={handleAddImage}
            ref={inputFileRef}
            multiple
            accept=".jpg,.png,.jpeg,.docx,.doc,.ppt,.txt,.csv,.pdf,.pptx,.xlsx,.xls"
          />
          <button
            type="button"
            onClick={() => inputFileRef.current?.click()}
            disabled={loading}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5 disabled:opacity-40 transition-colors"
            title="Joindre des fichiers"
          >
            <img
              className="w-5 h-5 dark:invert opacity-70 hover:opacity-100 transition-opacity"
              src={image && image.length ? ClipEngage : Clip}
              alt="Joindre"
            />
          </button>

          {/* Text Input */}
          <input
            type="text"
            className="flex-1 bg-transparent px-2 py-1.5 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none"
            value={input}
            disabled={loading}
            placeholder="Posez votre question ici..."
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />

          {/* Send / Stop Button */}
          {!fetching ? (
            <button
              type="button"
              onClick={handleSend}
              disabled={Boolean(!input.trim() && (!image || !image.length)) || loading}
              className="w-9 h-9 min-w-[2.25rem] rounded-xl flex items-center justify-center transition-all duration-200 bg-gradient-to-tr from-purple-600 via-indigo-600 to-cyan-500 hover:opacity-95 text-white disabled:opacity-30 disabled:cursor-not-allowed shadow-md hover:shadow-glow-purple"
              title="Envoyer"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              onClick={cancelApi}
              className="w-9 h-9 min-w-[2.25rem] rounded-xl flex items-center justify-center bg-red-500/20 text-red-500 border border-red-500/30 hover:bg-red-500/30 transition-colors"
              title="Interrompre la génération"
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Helper text below input */}
      <div className="mt-2 text-center text-[11px] text-slate-400 dark:text-slate-500">
        <span>⚡ Entrée pour envoyer • Maj + Entrée pour un saut de ligne</span>
      </div>
    </div>
  );
};

export default Input;
